import { useEffect } from 'react';
import { useRouter } from 'next/router';

import clsx from 'clsx';

// Hooks
import useUser from '@/hooks/swr/useUser';
import useFormatDateTime from '@/utils/hooks/format/useFormatDateTime';
import useMarkAsReadNotification from '@/utils/hooks/notifications/useMarkAsReadNotification';
import useSelectedSocialProfileById from '@/utils/hooks/socialprofiles/useSelectedSocialProfileById';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import type { SocialProfileType } from '@/types/socialProfileType';
// Types
import type { NotificationCollaborationProps } from '@/utils/hooks/context/toast/types';

// Lib
import { APPROVED, REJECTED } from '@/lib/scheduler/postStatus';

// Component
import Icon from '@/components/base/Icon';
import Notification from '@/components/base/Notification';
import PostThumbnail from '@/components/composite/Notifications/Collaboration/components/PostThumbnail';

// Routes
import { SCHEDULER } from '@/routes/privateRoutes';

const PostStatusChanged = ({ notification }: NotificationCollaborationProps) => {
    const { user } = useUser();

    const router = useRouter();
    const { setActiveSocialProfile } = useActiveSocialProfile();
    const { markAsReadCollaborationNotification } = useMarkAsReadNotification();
    const { selectedSocialProfile } = useSelectedSocialProfileById(
        notification.data?.social_profile_id as SocialProfileType['id']
    );

    const postStatus: typeof APPROVED | typeof REJECTED =
        notification.data?.scheduler_post?.status ?? APPROVED;

    const { formatDateTime } = useFormatDateTime(user?.settings?.timezone ?? 'Europe/Paris');

    const url = `${SCHEDULER}?post_id=${notification.data?.scheduler_post?.id}&social_profile_id=${notification.data?.social_profile_id}`;
    // Prefetch Overview
    useEffect(() => {
        router.prefetch(url);
    }, []);

    const userTimeFormat: '24' | '12' = user?.settings?.time_format ?? '24';

    return (
        <Notification
            onClick={() => {
                markAsReadCollaborationNotification(notification);
                if (selectedSocialProfile) {
                    setActiveSocialProfile(selectedSocialProfile);
                    router.push(url);
                }
            }}
            dataNotification={notification}
            contentHidden={notification?.options?.hideContent ?? false}
            wide={notification?.options?.wide ?? true}>
            <div className="relative">
                <PostThumbnail
                    thumbnail={notification.data?.scheduler_post?.media_thumbnail}
                    postType={notification.data?.scheduler_post?.type ?? 'image'}
                />
                <div
                    className={clsx(
                        'absolute -right-1 -top-1 flex h-3.5 w-3.5 items-center justify-center rounded-full text-8 text-white',
                        {
                            'bg-icoBlue': postStatus === APPROVED,
                            'bg-icoRed': postStatus === REJECTED
                        }
                    )}>
                    <Icon icon={postStatus === REJECTED ? 'rejected' : 'check'} />
                </div>
                <div className="absolute -bottom-1.5 -right-1.5">
                    <Notification.Avatar size="xs" socialProfile={selectedSocialProfile} />
                </div>
            </div>
            <Notification.Content>
                <Notification.Title
                    i18nKey={`commons:notifications.scheduler_post_status_changed.${notification?.data?.scheduler_post?.status === REJECTED
                            ? 'title_rejected'
                            : 'title_approved'
                        }`}
                    values={{ name: notification?.data?.username }}
                />
                <Notification.Description
                    i18nKey="commons:notifications.scheduler_post_status_changed.description"
                    values={{
                        date: formatDateTime(
                            notification.data?.scheduler_post?.scheduled_at,
                            user?.settings?.date_format
                        ),
                        hour: formatDateTime(
                            notification.data?.scheduler_post?.scheduled_at,
                            `${userTimeFormat}_hour_minutes`
                        )
                    }}
                />
            </Notification.Content>
        </Notification>
    );
};

export default PostStatusChanged;
